import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RestaurantLayout } from "@/components/tabdine/RestaurantLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, Plus, EyeOff, Eye } from "lucide-react";

const TYPES = [
  { v: "custom_html", label: "Custom HTML / Text" },
  { v: "gallery", label: "Photo Gallery" },
  { v: "link", label: "External Link" },
];

export default function PageTabsManager() {
  const [tabs, setTabs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ tab_label: "", tab_type: "custom_html", is_visible: true, content: {} });

  const load = () => api.get("/restaurant/page-tabs").then((r) => setTabs(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ tab_label: "", tab_type: "custom_html", is_visible: true, content: {}, sort_order: 99 });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ ...t, content: t.content || {} });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.patch(`/restaurant/page-tabs/${editing.id}`, form);
      else await api.post("/restaurant/page-tabs", form);
      setOpen(false);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (t) => {
    if (t.is_default) return toast.error("Default tab can't be deleted");
    if (!confirm(`Delete tab "${t.tab_label}"?`)) return;
    await api.delete(`/restaurant/page-tabs/${t.id}`);
    load();
  };

  const toggle = async (t) => {
    await api.patch(`/restaurant/page-tabs/${t.id}`, { is_visible: !t.is_visible });
    load();
  };

  const move = async (t, dir) => {
    const list = [...tabs].sort((a, b) => a.sort_order - b.sort_order);
    const i = list.findIndex((x) => x.id === t.id);
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[i], b = list[j];
    await Promise.all([
      api.patch(`/restaurant/page-tabs/${a.id}`, { sort_order: b.sort_order }),
      api.patch(`/restaurant/page-tabs/${b.id}`, { sort_order: a.sort_order }),
    ]);
    load();
  };

  return (
    <RestaurantLayout>
      <div className="p-4 lg:p-8 max-w-3xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-brand-dark">Page Tabs</h1>
            <div className="text-sm text-brand-dark/60">Control what customers see when they visit your page.</div>
          </div>
          <Button onClick={openNew} className="bg-brand-orange text-white" data-testid="new-tab">
            <Plus size={14} className="mr-1" /> New tab
          </Button>
        </div>

        <div className="space-y-2">
          {tabs.map((t, i) => (
            <div key={t.id} className="bg-white rounded-lg border border-brand-dark/5 p-3 flex items-center gap-3" data-testid={`tab-row-${t.tab_key}`}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => move(t, -1)} disabled={i === 0} className="text-xs text-brand-dark/60 disabled:opacity-30">▲</button>
                <button onClick={() => move(t, 1)} disabled={i === tabs.length - 1} className="text-xs text-brand-dark/60 disabled:opacity-30">▼</button>
              </div>
              <div className="flex-1">
                <div className="font-medium text-brand-dark">{t.tab_label}</div>
                <div className="text-xs text-brand-dark/50">
                  {t.tab_type}
                  {t.is_default && <span className="ml-1 px-1.5 py-0.5 rounded bg-brand-dark/10">Default</span>}
                </div>
              </div>
              <button onClick={() => toggle(t)} className="text-brand-dark/70 hover:text-brand-orange" data-testid={`toggle-${t.tab_key}`}>
                {t.is_visible ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
              {!t.is_default && (
                <>
                  <button onClick={() => openEdit(t)} className="text-xs text-brand-dark/70 hover:text-brand-orange">Edit</button>
                  <button onClick={() => del(t)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit tab" : "New tab"}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-xs text-brand-dark/60">Label</label>
              <Input value={form.tab_label} onChange={(e) => setForm({ ...form, tab_label: e.target.value })} data-testid="tab-label" />
            </div>
            {!editing && (
              <div>
                <label className="text-xs text-brand-dark/60">Type</label>
                <select value={form.tab_type} onChange={(e) => setForm({ ...form, tab_type: e.target.value })} className="w-full h-10 rounded border border-input px-2">
                  {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </div>
            )}
            {form.tab_type === "custom_html" && (
              <div>
                <label className="text-xs text-brand-dark/60">Content (HTML supported)</label>
                <textarea
                  value={form.content?.html || ""}
                  onChange={(e) => setForm({ ...form, content: { ...form.content, html: e.target.value } })}
                  className="w-full min-h-[120px] rounded border border-input p-2"
                  data-testid="tab-content-html"
                />
              </div>
            )}
            {form.tab_type === "link" && (
              <div>
                <label className="text-xs text-brand-dark/60">External URL</label>
                <Input value={form.content?.external_url || ""} onChange={(e) => setForm({ ...form, content: { ...form.content, external_url: e.target.value } })} />
              </div>
            )}
            {form.tab_type === "gallery" && (
              <div>
                <label className="text-xs text-brand-dark/60">Image URLs (comma-separated)</label>
                <textarea
                  value={(form.content?.images || []).join(", ")}
                  onChange={(e) => setForm({ ...form, content: { ...form.content, images: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                  className="w-full min-h-[80px] rounded border border-input p-2"
                />
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_visible} onChange={(e) => setForm({ ...form, is_visible: e.target.checked })} /> Visible on customer page
            </label>
            <Button onClick={save} className="w-full bg-brand-orange text-white" data-testid="save-tab">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </RestaurantLayout>
  );
}
